import "./Quiz.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
  Alert,
  Button as AntButton,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Switch,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { Quizzes } from "../../api/Quiz";
import { Quiz, QuizDTO } from "../../types/Quiz";

type QuestionForm = {
  id?: string;
  text: string;
  points: number;
  answers: string[];
  correctIndex: number;
};

type FormValues = {
  title: string;
  description?: string;
  period: [Dayjs, Dayjs];
  active: boolean;
  questions: QuestionForm[];
};

const emptyQuestion = (): QuestionForm => ({
  text: "",
  points: 1,
  answers: ["", ""],
  correctIndex: 0,
});

// Dal formato salvato (id + correctAnswerId) al formato del form (indice).
const quizToForm = (quiz: Quiz): FormValues => ({
  title: quiz.title,
  description: quiz.description || "",
  period: [dayjs(quiz.startDate), dayjs(quiz.endDate)],
  active: quiz.active,
  questions: quiz.questions.map((question) => ({
    id: question.id,
    text: question.text,
    points: question.points,
    answers: question.answers.map((answer) => answer.text),
    correctIndex: Math.max(
      0,
      question.answers.findIndex((answer) => answer.id === question.correctAnswerId)
    ),
  })),
});

export default function QuizEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [quiz, setQuiz] = useState<Quiz>();
  const [loadError, setLoadError] = useState(false);

  const isEdit = !!id;
  // Con partecipazioni le domande sono congelate (lo impone anche il BE).
  const questionsLocked = useMemo(
    () => !!quiz && quiz.submissions > 0,
    [quiz]
  );

  useEffect(() => {
    if (!id) return;
    Quizzes.getQuiz(id).then(
      ({ data }) => {
        setQuiz(data.quiz);
        form.setFieldsValue(quizToForm(data.quiz));
      },
      () => {
        setLoadError(true);
        showToast("quiz-load-error", "Quiz non trovato", "", "danger");
      }
    );
  }, [id, form, showToast]);

  const onFinish = useCallback(
    (values: FormValues) => {
      const body: QuizDTO = {
        title: values.title.trim(),
        description: values.description?.trim() || null,
        startDate: values.period[0].toISOString(),
        endDate: values.period[1].toISOString(),
        active: values.active,
        questions: values.questions.map((question) => ({
          id: question.id,
          text: question.text.trim(),
          points: question.points,
          answers: question.answers.map((answer) => answer.trim()),
          correctIndex: question.correctIndex,
        })),
      };

      setLoading(true);
      const request = isEdit
        ? Quizzes.updateQuiz(id!, body)
        : Quizzes.createQuiz(body);
      request.then(
        ({ data }) => {
          setLoading(false);
          showToast(
            "quiz-save-success",
            isEdit ? "Quiz aggiornato" : "Quiz creato",
            "",
            "success"
          );
          navigate(`/quiz/${data.quiz.id}`);
        },
        (err) => {
          setLoading(false);
          showToast(
            "quiz-save-error",
            err?.response?.data?.message ||
              "Errore nel salvataggio del quiz, per favore riprova.",
            "",
            "danger"
          );
        }
      );
    },
    [isEdit, id, navigate, showToast]
  );

  if (isEdit && !quiz && !loadError) {
    return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  }

  return (
    <Container fluid="md" className="mt-5 mb-5">
      <Row className="mb-2 align-items-center">
        <Col xs="auto">
          <AntButton
            type="text"
            icon={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={() => navigate(isEdit ? `/quiz/${id}` : "/quiz")}
          />
        </Col>
        <Col>
          <h4 className="mb-0">{isEdit ? "Modifica Quiz" : "Nuovo Quiz"}</h4>
        </Col>
        <Col xs={6} md={4} className="text-end">
          <button
            type="button"
            disabled={loading || loadError}
            onClick={() => form.submit()}
            className={classNames("button-primary", {
              "button-disabled": loading || loadError,
            })}
          >
            {loading ? (
              <TailSpin color="#FFF" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span className="d-none d-sm-inline ms-2">Salva</span>
              </>
            )}
          </button>
        </Col>
      </Row>
      <hr />

      {loadError ? (
        <Alert type="error" message="Quiz non trovato" showIcon />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            active: true,
            questions: [emptyQuestion()],
          }}
        >
          <Row>
            <Col xs={12}>
              <h6 className="text-primary mt-3">Informazioni generali</h6>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item
                label="Titolo"
                name="title"
                rules={[
                  { required: true, message: "Inserisci un titolo" },
                  { whitespace: true, message: "Inserisci un titolo valido" },
                ]}
              >
                <Input placeholder="Es. Quiz di Padel della settimana" maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item
                label="Attivo"
                name="active"
                valuePropName="checked"
                tooltip="Un quiz disattivato non compare nell'app anche se nel periodo di validità"
              >
                <Switch checkedChildren="Sì" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Descrizione (facoltativa)" name="description">
                <Input.TextArea
                  rows={2}
                  maxLength={1000}
                  showCount
                  placeholder="Testo introduttivo mostrato agli utenti prima di iniziare"
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item
                label="Periodo di validità"
                name="period"
                rules={[{ required: true, message: "Indica inizio e fine del quiz" }]}
              >
                <DatePicker.RangePicker
                  className="w-100"
                  showTime={{ format: "HH:mm" }}
                  format="DD/MM/YYYY HH:mm"
                  placeholder={["Inizio", "Fine"]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col xs={12}>
              <h6 className="text-primary">Domande</h6>
              {questionsLocked ? (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-3"
                  message={`Il quiz ha già ${quiz!.submissions} ${
                    quiz!.submissions === 1 ? "partecipazione" : "partecipazioni"
                  }: le domande non si possono più modificare. Puoi cambiare titolo, descrizione, periodo e stato.`}
                />
              ) : (
                <p className="text-muted">
                  Ogni domanda ha da 2 a 10 risposte, una sola corretta, e vale
                  i Get Move Points indicati.
                </p>
              )}
            </Col>
          </Row>

          <Form.List
            name="questions"
            rules={[
              {
                validator: async (_, questions) => {
                  if (!questions || questions.length < 1) {
                    return Promise.reject(new Error("Inserisci almeno una domanda"));
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <div className="quiz-question-card" key={field.key}>
                    <div className="quiz-question-header">
                      <span className="quiz-question-index">
                        Domanda {index + 1}
                      </span>
                      {!questionsLocked && fields.length > 1 && (
                        <AntButton
                          type="text"
                          danger
                          size="small"
                          icon={<FontAwesomeIcon icon={faTrash} />}
                          onClick={() => remove(field.name)}
                        >
                          Rimuovi
                        </AntButton>
                      )}
                    </div>
                    {/* L'id della domanda viene conservato in modifica. */}
                    <Form.Item name={[field.name, "id"]} hidden>
                      <Input />
                    </Form.Item>
                    <Row>
                      <Col xs={12} md={9}>
                        <Form.Item
                          label="Testo della domanda"
                          name={[field.name, "text"]}
                          rules={[
                            { required: true, message: "Inserisci la domanda" },
                            { whitespace: true, message: "Inserisci una domanda valida" },
                          ]}
                        >
                          <Input.TextArea
                            rows={2}
                            maxLength={500}
                            disabled={questionsLocked}
                            placeholder="Es. In quale anno è nato il padel?"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Item
                          label="Get Move Points"
                          name={[field.name, "points"]}
                          rules={[
                            { required: true, message: "Inserisci i punti" },
                            { type: "number", min: 0, message: "Punti non validi" },
                          ]}
                        >
                          <InputNumber
                            className="w-100"
                            min={0}
                            step={1}
                            precision={0}
                            disabled={questionsLocked}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div className="quiz-answer-hint">
                      Risposte: seleziona il pallino accanto a quella corretta.
                    </div>
                    <Form.Item
                      name={[field.name, "correctIndex"]}
                      rules={[
                        {
                          validator: async (_, value) => {
                            const answers: string[] =
                              form.getFieldValue(["questions", field.name, "answers"]) || [];
                            if (typeof value !== "number" || value >= answers.length) {
                              return Promise.reject(new Error("Indica la risposta corretta"));
                            }
                          },
                        },
                      ]}
                    >
                      <Radio.Group className="w-100" disabled={questionsLocked}>
                        <Form.List
                          name={[field.name, "answers"]}
                          rules={[
                            {
                              validator: async (_, answers) => {
                                if (!answers || answers.length < 2) {
                                  return Promise.reject(
                                    new Error("Inserisci almeno 2 risposte")
                                  );
                                }
                              },
                            },
                          ]}
                        >
                          {(answerFields, answerOps, answerMeta) => (
                            <>
                              {answerFields.map((answerField, answerIndex) => (
                                <div className="quiz-answer-row" key={answerField.key}>
                                  <Radio value={answerIndex} />
                                  <Form.Item
                                    name={answerField.name}
                                    rules={[
                                      { required: true, message: "Inserisci la risposta" },
                                      { whitespace: true, message: "Inserisci una risposta valida" },
                                    ]}
                                  >
                                    <Input
                                      maxLength={300}
                                      disabled={questionsLocked}
                                      placeholder={`Risposta ${answerIndex + 1}`}
                                    />
                                  </Form.Item>
                                  {!questionsLocked && answerFields.length > 2 && (
                                    <AntButton
                                      type="text"
                                      danger
                                      icon={<FontAwesomeIcon icon={faTrash} />}
                                      onClick={() => {
                                        // Se rimuovo la risposta corretta (o una
                                        // precedente) riallineo l'indice.
                                        const current: number =
                                          form.getFieldValue(["questions", field.name, "correctIndex"]) ?? 0;
                                        answerOps.remove(answerField.name);
                                        if (current === answerIndex) {
                                          form.setFieldValue(["questions", field.name, "correctIndex"], 0);
                                        } else if (current > answerIndex) {
                                          form.setFieldValue(["questions", field.name, "correctIndex"], current - 1);
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              ))}
                              {!questionsLocked && answerFields.length < 10 && (
                                <AntButton
                                  size="small"
                                  icon={<FontAwesomeIcon icon={faPlus} />}
                                  onClick={() => answerOps.add("")}
                                >
                                  Aggiungi risposta
                                </AntButton>
                              )}
                              <Form.ErrorList errors={answerMeta.errors} />
                            </>
                          )}
                        </Form.List>
                      </Radio.Group>
                    </Form.Item>
                  </div>
                ))}
                {!questionsLocked && fields.length < 50 && (
                  <AntButton
                    type="dashed"
                    block
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => add(emptyQuestion())}
                  >
                    Aggiungi domanda
                  </AntButton>
                )}
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Container>
  );
}
